import React, { useEffect, useMemo } from 'react'
import * as yup from 'yup'
import { useTheme } from 'styled-components'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import useIntl from 'hooks/useIntl'
import { Wizard, WizardProps } from 'components/Wizard'
import {
	resetOnboardingStore,
	useOnboardingStore
} from 'screens/Onboarding/hooks/useOnboardingStore'
import HelpGuideDrawer, { HelpGuideDrawerProps } from 'components/HelpGuideDrawer'

import { FreeFormType } from './FreeForm.types'
import FreeFormSkillsSelection from './containers/FreeFormSkillsSelection'
import FreeFormChallengePreview from './containers/FreeFormChallengePreview'
import FreeFormTeamCreation from './containers/FreeFormTeamCreation'
import { resetFreeFormStore } from './hooks/useFreeFormStore'

const formSchema = yup.object().shape({
	team: yup.object().shape({
		name: yup.string().required(),
		maskChallenge: yup.bool()
	}),
	talentGroup: yup.object().shape({
		name: yup.string().required(),
		type: yup.string().required(),
		color: yup.string().required(),
		teammateEmails: yup.array().of(yup.string().email())
	})
})

export const FreeForm = () => {
	const intl = useIntl()
	const theme = useTheme()
	const { currentStep, showHelpGuide, toggleHelpGuide } = useOnboardingStore(state => state)

	const formMethods = useForm<FreeFormType>({
		mode: 'all',
		shouldUnregister: false,
		resolver: yupResolver(formSchema),
		defaultValues: {
			challengePack: 'free-form',
			chapters: [],
			team: {
				name: '',
				maskChallenge: false
			},
			talentGroup: {
				name: '',
				type: '',
				color: theme.tag.blue,
				teammateEmails: []
			}
		}
	})

	useEffect(
		() => () => {
			resetOnboardingStore()
			resetFreeFormStore()
		},
		[]
	)

	const steps: WizardProps['stepperLabels'] = useMemo(
		() => [
			{
				label: intl.formatMessage({ id: 'onboarding.free.form.step.0' })
			},
			{
				label: intl.formatMessage({ id: 'onboarding.free.form.step.1' })
			},
			{
				label: intl.formatMessage({ id: 'onboarding.free.form.step.2' })
			}
		],
		[intl]
	)

	const guides: HelpGuideDrawerProps['guides'] = useMemo(
		() =>
			Array.from({ length: 3 }, (_, index) => ({
				title: intl.formatMessage({
					id: `onboarding.free.form.help.guide.title.step.${index}`
				}),
				desc: intl.formatMessage({
					id: `onboarding.free.form.help.guide.desc.step.${index}`
				})
			})),
		[intl]
	)

	const renderStep = () => {
		switch (currentStep) {
			case 0:
				return <FreeFormSkillsSelection />
			case 1:
				return <FreeFormChallengePreview />
			case 2:
				return <FreeFormTeamCreation />
			default:
				return null
		}
	}

	return (
		<>
			<Wizard
				levelContentWithStepper
				currentStep={currentStep}
				previousPath="/"
				previousPageName={intl.formatMessage({ id: 'dashboard' }).toLowerCase()}
				stepperLabels={steps}
				showMobileStepIndicator={false}
			>
				<FormProvider {...formMethods}>{renderStep()}</FormProvider>
			</Wizard>
			<HelpGuideDrawer
				open={showHelpGuide}
				stepCount={currentStep}
				onClose={toggleHelpGuide}
				guides={guides}
			/>
		</>
	)
}

export default FreeForm
